import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { MessageSquare, Plus, Search, Filter, Copy, Trash2, Pencil, Check, X, Mail, Phone, Send, Eye, RefreshCw, Download, Building2, Trophy, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const CHANNELS = [
  { value: "whatsapp", label: "WhatsApp", icon: Send, color: "bg-green-100 text-green-700 border-green-200" },
  { value: "sms", label: "SMS", icon: Phone, color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "email", label: "Email", icon: Mail, color: "bg-purple-100 text-purple-700 border-purple-200" },
];

const STATUSES = [
  { value: "draft", label: "Draft", color: "bg-slate-100 text-slate-600" },
  { value: "pending_approval", label: "Pending Approval", color: "bg-amber-100 text-amber-700" },
  { value: "approved", label: "Approved", color: "bg-emerald-100 text-emerald-700" },
  { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-700" },
];

const CATEGORIES = [
  "lead_capture", "first_contact", "qualification", "site_visit",
  "post_visit", "negotiation", "booking", "lost_lead", "nurture",
  "internal", "transactional", "promotional"
];

const AVAILABLE_VARIABLES = [
  { key: "name", desc: "Lead name" },
  { key: "project", desc: "Project of interest" },
  { key: "phone", desc: "Phone number" },
  { key: "email", desc: "Email address" },
  { key: "agent_name", desc: "Assigned rep name" },
  { key: "company_name", desc: "Company name" },
  { key: "visit_date", desc: "Visit date" },
  { key: "visit_time", desc: "Visit time" },
  { key: "site_address", desc: "Site address" },
  { key: "unit_type", desc: "Unit configuration" },
  { key: "amount", desc: "Payment amount" },
  { key: "due_date", desc: "Payment due date" },
  { key: "festival_name", desc: "Festival name" },
];

const emptyForm = { name: "", channel: "whatsapp", category: "lead_capture", content: "", subject: "", language: "en", status: "draft", external_template_id: "" };

export default function MessageTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  const fetchLeaderboard = async () => {
    setLeaderboardLoading(true);
    try {
      const res = await axios.get(`${API}/message-templates/leaderboard?limit=50`);
      setLeaderboard(res.data?.templates || []);
    } catch { toast.error("Failed to load leaderboard"); }
    finally { setLeaderboardLoading(false); }
  };

  useEffect(() => {
    if (activeTab === "leaderboard") fetchLeaderboard();
  }, [activeTab]);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/message-templates`);
      setTemplates(res.data);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const filtered = useMemo(() => {
    let list = templates;
    if (channelFilter !== "all") list = list.filter(t => t.channel === channelFilter);
    if (statusFilter !== "all") list = list.filter(t => t.status === statusFilter);
    if (categoryFilter !== "all") list = list.filter(t => t.category === categoryFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(t => t.name.toLowerCase().includes(s) || t.content.toLowerCase().includes(s));
    }
    return list;
  }, [templates, channelFilter, statusFilter, categoryFilter, search]);

  const stats = useMemo(() => ({
    total: templates.length,
    approved: templates.filter(t => t.status === "approved").length,
    pending: templates.filter(t => t.status === "pending_approval").length,
    whatsapp: templates.filter(t => t.channel === "whatsapp").length,
    sms: templates.filter(t => t.channel === "sms").length,
    email: templates.filter(t => t.channel === "email").length,
  }), [templates]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.content.trim()) {
      toast.error("Name and content are required");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/message-templates/${editingId}`, form);
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/message-templates`, form);
        toast.success("Template created");
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ ...emptyForm });
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const handleEdit = (tpl) => {
    setEditingId(tpl.template_id);
    setForm({ name: tpl.name, channel: tpl.channel, category: tpl.category, content: tpl.content, subject: tpl.subject || "", language: tpl.language || "en", status: tpl.status, external_template_id: tpl.external_template_id || "" });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/message-templates/${id}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/message-templates/${id}/duplicate`);
      toast.success("Template duplicated");
      fetchTemplates();
    } catch { toast.error("Failed to duplicate"); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/message-templates/${id}`, { status: newStatus });
      toast.success(`Status changed to ${newStatus.replace("_", " ")}`);
      fetchTemplates();
    } catch { toast.error("Failed to update status"); }
  };

  const handleSyncDoubleTick = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/whatsapp/doubletick/sync-templates`);
      const { synced, skipped, total_from_doubletick } = res.data;
      toast.success(`Synced ${synced} new templates from DoubleTick (${skipped} already exist, ${total_from_doubletick} total)`);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sync DoubleTick templates");
    } finally {
      setSyncing(false);
    }
  };

  const insertVariable = (varKey) => {
    setForm(prev => ({ ...prev, content: prev.content + `{${varKey}}` }));
  };

  const getChannelInfo = (ch) => CHANNELS.find(c => c.value === ch) || CHANNELS[0];
  const getStatusInfo = (s) => STATUSES.find(st => st.value === s) || STATUSES[0];

  const resolvePreview = (content) => {
    const sampleVars = { name: "Rahul Sharma", project: "Sampoorna Homes", phone: "9876543210", email: "rahul@email.com", agent_name: "Vinod Mishra", company_name: "Rajat Group", visit_date: "15 Apr 2026", visit_time: "11:00 AM", site_address: "Plot 42, Sector 7, Raipur", unit_type: "3 BHK", amount: "5,00,000", due_date: "30 Apr 2026", festival_name: "Diwali" };
    let resolved = content;
    Object.entries(sampleVars).forEach(([k, v]) => { resolved = resolved.replace(new RegExp(`\\{${k}\\}`, "g"), v); });
    return resolved;
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="message-templates-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope']">Message Templates</h1>
            <p className="text-sm text-slate-500 mt-1">Manage approved templates for WhatsApp, SMS, and Email communications</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSyncDoubleTick} disabled={syncing} data-testid="sync-doubletick-btn" className="border-green-300 text-green-700 hover:bg-green-50">
              {syncing ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              {syncing ? "Syncing..." : "Sync DoubleTick"}
            </Button>
            <Button onClick={() => { setEditingId(null); setForm({ ...emptyForm }); setDialogOpen(true); }} data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-1" /> New Template
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card className="bg-white border-slate-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-slate-900">{stats.total}</p><p className="text-xs text-slate-500">Total</p></CardContent></Card>
          <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-emerald-700">{stats.approved}</p><p className="text-xs text-emerald-600">Approved</p></CardContent></Card>
          <Card className="bg-amber-50 border-amber-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-700">{stats.pending}</p><p className="text-xs text-amber-600">Pending</p></CardContent></Card>
          <Card className="bg-green-50 border-green-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-700">{stats.whatsapp}</p><p className="text-xs text-green-600">WhatsApp</p></CardContent></Card>
          <Card className="bg-blue-50 border-blue-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{stats.sms}</p><p className="text-xs text-blue-600">SMS</p></CardContent></Card>
          <Card className="bg-purple-50 border-purple-200"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-purple-700">{stats.email}</p><p className="text-xs text-purple-600">Email</p></CardContent></Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList data-testid="tpl-tabs">
            <TabsTrigger value="all" data-testid="tab-all">All Templates</TabsTrigger>
            <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
              <Trophy className="w-3.5 h-3.5 mr-1" /> Winning Scripts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-templates" />
          </div>
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="w-[140px]" data-testid="filter-channel"><Filter className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              {CHANNELS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px]" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Template List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading templates...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400">No templates found. Create your first template!</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(tpl => {
              const ch = getChannelInfo(tpl.channel);
              const st = getStatusInfo(tpl.status);
              const ChIcon = ch.icon;
              return (
                <Card key={tpl.template_id} className="bg-white border-slate-200 hover:shadow-md transition-shadow" data-testid={`template-card-${tpl.template_id}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${ch.color}`}>
                          <ChIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{tpl.name}</p>
                          <p className="text-[10px] text-slate-400 capitalize">{tpl.category?.replace(/_/g, " ")} &middot; {tpl.language?.toUpperCase()}</p>
                        </div>
                      </div>
                      <Badge className={`text-[10px] shrink-0 ${st.color}`}>{st.label}</Badge>
                    </div>

                    {tpl.doubletick_template_name && (
                      <div className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 rounded px-2 py-0.5 w-fit">
                        <Send className="w-3 h-3" />
                        <span>DoubleTick: {tpl.doubletick_template_name}</span>
                      </div>
                    )}

                    <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 leading-relaxed max-h-[80px] overflow-hidden">
                      {tpl.content}
                    </div>

                    {tpl.variables?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {tpl.variables.map(v => <Badge key={v} variant="outline" className="text-[9px] text-slate-500">{`{${v}}`}</Badge>)}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                      <div className="flex gap-1">
                        {tpl.status === "draft" && (
                          <Button size="sm" variant="ghost" className="h-7 text-[10px] text-amber-600" onClick={() => handleStatusChange(tpl.template_id, "pending_approval")} data-testid={`submit-approval-${tpl.template_id}`}>
                            Submit for Approval
                          </Button>
                        )}
                        {tpl.status === "pending_approval" && (
                          <>
                            <Button size="sm" variant="ghost" className="h-7 text-[10px] text-emerald-600" onClick={() => handleStatusChange(tpl.template_id, "approved")} data-testid={`approve-${tpl.template_id}`}>
                              <Check className="w-3 h-3 mr-0.5" /> Approve
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[10px] text-red-600" onClick={() => handleStatusChange(tpl.template_id, "rejected")} data-testid={`reject-${tpl.template_id}`}>
                              <X className="w-3 h-3 mr-0.5" /> Reject
                            </Button>
                          </>
                        )}
                      </div>
                      <div className="flex gap-0.5">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setPreviewTemplate(tpl); setPreviewOpen(true); }} data-testid={`preview-${tpl.template_id}`}>
                          <Eye className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEdit(tpl)} data-testid={`edit-${tpl.template_id}`}>
                          <Pencil className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDuplicate(tpl.template_id)} data-testid={`duplicate-${tpl.template_id}`}>
                          <Copy className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(tpl.template_id)} data-testid={`delete-${tpl.template_id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
          </TabsContent>

          <TabsContent value="leaderboard" className="space-y-4">
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">Scripts That Actually Convert</p>
                  <p className="text-xs text-amber-800/80 mt-0.5">
                    These templates came from Nurture touchpoints where a rep logged
                    <span className="font-semibold"> "done — buyer engaged"</span>. Rolled up across every buyer it worked on.
                  </p>
                </div>
              </CardContent>
            </Card>

            {leaderboardLoading ? (
              <div className="text-center py-12 text-slate-500" data-testid="leaderboard-loading">Loading leaderboard…</div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-12 text-slate-400" data-testid="leaderboard-empty">
                No winning scripts promoted yet. Reps can promote scripts from the Nurture Journey on any Lead Detail page once a buyer engages.
              </div>
            ) : (
              <div className="space-y-2" data-testid="leaderboard-list">
                {leaderboard.map((tpl, i) => {
                  const ch = getChannelInfo(tpl.channel);
                  const ChIcon = ch.icon;
                  const st = getStatusInfo(tpl.status);
                  const rankColor = i === 0
                    ? "bg-amber-500 text-white"
                    : i === 1 ? "bg-slate-400 text-white"
                    : i === 2 ? "bg-orange-700 text-white"
                    : "bg-slate-100 text-slate-600";
                  return (
                    <Card key={tpl.template_id} className="bg-white border-slate-200 hover:shadow-sm transition-shadow"
                          data-testid={`leaderboard-row-${tpl.template_id}`}>
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${rankColor}`}>
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <div className={`w-6 h-6 rounded-md flex items-center justify-center ${ch.color}`}>
                              <ChIcon className="w-3 h-3" />
                            </div>
                            <p className="text-sm font-semibold text-slate-900 truncate">{tpl.name}</p>
                            <Badge className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                          </div>
                          <p className="text-xs text-slate-600 whitespace-pre-wrap line-clamp-3">{tpl.content}</p>
                          <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-2 flex-wrap">
                            <span className="flex items-center gap-1 text-amber-700 font-semibold">
                              <Trophy className="w-3 h-3" /> {tpl.source_wins || 0} win{(tpl.source_wins || 0) === 1 ? "" : "s"}
                            </span>
                            <span>· promoted {tpl.promotion_count || 1}×</span>
                            {tpl.source_day && <span>· from Day {tpl.source_day} {tpl.source_channel?.replace(/_/g, " ")}</span>}
                            {tpl.promoted_by_name && <span>· by {tpl.promoted_by_name}</span>}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 items-end shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                                  onClick={() => { setPreviewTemplate(tpl); setPreviewOpen(true); }}
                                  data-testid={`leaderboard-preview-${tpl.template_id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                          </Button>
                          {tpl.status === "pending_approval" && (
                            <Button size="sm" variant="ghost"
                                    className="h-7 text-[11px] text-emerald-600"
                                    onClick={async () => {
                                      await handleStatusChange(tpl.template_id, "approved");
                                      fetchLeaderboard();
                                    }}
                                    data-testid={`leaderboard-approve-${tpl.template_id}`}>
                              <Check className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">{editingId ? "Edit Template" : "New Message Template"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Template Name</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Welcome - New Lead" data-testid="form-name" />
              </div>
              <div>
                <Label>Channel</Label>
                <Select value={form.channel} onValueChange={v => setForm({...form, channel: v})}>
                  <SelectTrigger data-testid="form-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHANNELS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Language</Label>
                <Select value={form.language} onValueChange={v => setForm({...form, language: v})}>
                  <SelectTrigger data-testid="form-language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="mr">Marathi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.channel === "email" && (
                <div className="col-span-2">
                  <Label>Email Subject</Label>
                  <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="e.g. Welcome to {project}!" data-testid="form-subject" />
                </div>
              )}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Content</Label>
                  <span className="text-[10px] text-slate-400">{form.content.length} chars</span>
                </div>
                <Textarea
                  value={form.content}
                  onChange={e => setForm({...form, content: e.target.value})}
                  placeholder="Type your message template here. Use {name}, {project} etc. for variables."
                  className="min-h-[120px] text-sm"
                  data-testid="form-content"
                />
                <div className="mt-2">
                  <p className="text-[10px] text-slate-500 mb-1">Insert variable:</p>
                  <div className="flex flex-wrap gap-1">
                    {AVAILABLE_VARIABLES.map(v => (
                      <Badge
                        key={v.key}
                        variant="outline"
                        className="text-[10px] cursor-pointer hover:bg-slate-100"
                        onClick={() => insertVariable(v.key)}
                        title={v.desc}
                      >
                        {`{${v.key}}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-span-2">
                <Label>External Template ID <span className="text-slate-400 font-normal">(optional — from your BSP)</span></Label>
                <Input value={form.external_template_id} onChange={e => setForm({...form, external_template_id: e.target.value})} placeholder="e.g. WhatsApp approved template ID" data-testid="form-external-id" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="save-template-btn">{editingId ? "Update" : "Create"} Template</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Message Preview
              </DialogTitle>
            </DialogHeader>
            {previewTemplate && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className={getChannelInfo(previewTemplate.channel).color}>{previewTemplate.channel.toUpperCase()}</Badge>
                  <Badge className={getStatusInfo(previewTemplate.status).color}>{previewTemplate.status}</Badge>
                </div>
                {previewTemplate.subject && (
                  <div>
                    <Label className="text-xs text-slate-500">Subject</Label>
                    <p className="text-sm font-medium">{resolvePreview(previewTemplate.subject)}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500">Message (with sample data)</Label>
                  <div className={`mt-1 p-4 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                    previewTemplate.channel === "whatsapp" ? "bg-green-50 border border-green-200" :
                    previewTemplate.channel === "sms" ? "bg-blue-50 border border-blue-200" :
                    "bg-purple-50 border border-purple-200"
                  }`}>
                    {resolvePreview(previewTemplate.content)}
                  </div>
                </div>
                {previewTemplate.variables?.length > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500">Variables used</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {previewTemplate.variables.map(v => <Badge key={v} variant="outline" className="text-[10px]">{`{${v}}`}</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
